import { useState, useEffect, useRef } from "react";
import { Box, Drawer, Typography, Divider } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import GaDocument from "./GaDocument";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeSlug from "rehype-slug";
import rehypeStringify from "rehype-stringify";
import { useNavigate, useParams } from "react-router-dom";
import { Chip } from "primereact/chip";
import { fetchAuthSession } from "aws-amplify/auth";
import { fetchDashboardDocumentation } from "../../api/dashboardService";

const DocumentViewer = () => {
  // State for document rendering and navigation
  const [content, setContent] = useState("");
  const [toc, setToc] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const documentContainerRef = useRef(null);

  // React Router hooks
  const navigate = useNavigate();
  const { documentId } = useParams();

  useEffect(() => {
    const fetchDocuments = async () => {
      // Only set loading if we don't already have documents
      if (documents.length === 0) {
        setLoading(true);
        setError(null);
      }
      try {
        // Get the authentication token
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;
        if (!token) throw new Error("User token is missing. Please log in.");

        // Fetch documentation from backend - only if we don't have documents yet
        if (documents.length === 0) {
          const response = await fetchDashboardDocumentation(token);

          // Check if the response contains documents array
          if (
            response &&
            response.success &&
            Array.isArray(response.documents) &&
            response.documents.length > 0
          ) {
            const docsData = response.documents;
            setDocuments(docsData);

            // If URL has a document ID, use that document
            if (documentId) {
              const doc = docsData.find((d) => d.id === documentId);
              if (doc) {
                setContent(doc.content);
                setLoading(false);
                return;
              }
            }

            // If no document ID in URL or document not found, use the first document
            if (docsData.length > 0) {
              const firstDoc = docsData[0];
              setContent(firstDoc.content);

              // Update URL without reloading - if we're at the base URL
              if (!documentId) {
                navigate(`/dashboard/documents/${firstDoc.id}`, {
                  replace: true,
                });
              }
            }
          } else {
            // Handle case when no documents are returned
            setError("No documentation available.");
          }
        } else {
          // We already have documents
          if (documentId) {
            // Set content based on documentId
            const doc = documents.find((d) => d.id === documentId);
            if (doc) {
              setContent(doc.content);
            } else {
              setError(`Document "${documentId}" not found.`);
            }
          } else {
            // No documentId in URL but we have documents - update URL to first document
            const firstDoc = documents[0];
            setContent(firstDoc.content);
            navigate(`/dashboard/documents/${firstDoc.id}`, {
              replace: true,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching documentation:", error);
        setError(error.message || "Unknown error occurred.");
      } finally {
        setLoading(false);
      }
    };
    // console.log("useEffect triggered with documentId");
    fetchDocuments();
  }, [navigate, documentId]);

  // Extract Table of Contents from content
  useEffect(() => {
    if (documentContainerRef.current) {
      documentContainerRef.current.scrollTop = 0;
    }

    const extractToc = () => (tree) => {
      try {
        if (!tree || !tree.children) {
          return;
        }

        const tocItems = [];
        tree.children.forEach((node, index) => {
          if (
            node.tagName === "h1" ||
            node.tagName === "h2" ||
            node.tagName === "h3"
          ) {
            const id =
              node.properties && node.properties.id
                ? node.properties.id
                : `toc-item-${index}`;
            const textNode = node.children.find(
              (child) => child.type === "text"
            );
            if (id && textNode) {
              tocItems.push({
                id,
                title: textNode.value,
                tagName: node.tagName,
              });
            }
          }
        });
        setToc(tocItems);
      } catch (error) {
        console.error("Error extracting TOC:", error);
      }
    };

    const processContent = async () => {
      try {
        const processor = unified()
          .use(remarkParse)
          .use(remarkRehype)
          .use(rehypeSlug)
          .use(extractToc)
          .use(rehypeStringify);

        await processor.process(content);
      } catch (error) {
        console.error("Error processing content:", error);
      }
    };

    processContent();
    setSelectedItem(null);
  }, [content]);

  const handleDocumentChange = (docId) => {
    navigate(`/dashboard/documents/${docId}`);
  };

  const handleTocClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setSelectedItem(id);
    }
  };

  // Render tree for TOC
  const renderTree = (items) => {
    return items.map((item, index) => (
      <TreeItem
        key={item.id || `toc-item-${index}`}
        itemId={item.id || `toc-item-${index}`}
        label={item.title}
        onClick={() => handleTocClick(item.id || `toc-item-${index}`)}
        sx={{
          cursor: "pointer",
          "& .MuiTreeItem-content .MuiTreeItem-iconContainer": {
            display: "none",
          },
          "& .MuiTreeItem-content .MuiTreeItem-label": {
            fontWeight:
              item.tagName === "h1"
                ? "bold"
                : item.tagName === "h2"
                ? "medium"
                : "normal",
            fontSize: "0.875rem",
          },
          "& .MuiTreeItem-label": {
            fontWeight:
              item.tagName === "h1"
                ? "bold"
                : item.tagName === "h2"
                ? "medium"
                : "normal",
            fontSize: "0.875rem",
            marginLeft:
              item.tagName === "h2" ? 2 : item.tagName === "h3" ? 4 : 0,
            color:
              selectedItem === (item.id || `toc-item-${index}`)
                ? "primary.main"
                : "inherit",
          },
          "& .Mui-selected .MuiTreeItem-label": {
            color: "primary.main",
            marginLeft:
              item.tagName === "h2" ? 2 : item.tagName === "h3" ? 4 : 0,
          },
          marginBottom: 0,
        }}
        selected={selectedItem === (item.id || `toc-item-${index}`)}
      />
    ));
  };

  if (error)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
          color: "#d32f2f", // Error color
        }}
      >
        <div style={{ fontSize: "1rem" }}>{error}</div>
      </div>
    );

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh", // Full viewport height
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "1.25rem" }}>Loading content...</div>
      </div>
    );

  return (
    <Box sx={{ display: "flex", flexGrow: 1 }}>
      <Box
        component="section"
        sx={{ flexGrow: 1, overflow: "auto", padding: 2 }}
        ref={documentContainerRef}
      >
        {/* Document Selection UI - Streamlined Design */}
        <h3
          className="p-text-secondary"
          style={{
            fontSize: "1rem",
            // marginBottom: "0.5rem",
            marginLeft: "4px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          Select a document:
        </h3>
        <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {documents.map((doc) => (
            <Chip
              key={doc.id}
              label={doc.title}
              onClick={() => handleDocumentChange(doc.id)}
              className={`document-selector ${
                doc.id === documentId ? "p-highlight" : ""
              }`}
              style={{
                cursor: "pointer",
                fontWeight: doc.id === documentId ? "600" : "normal",
                backgroundColor:
                  doc.id === documentId
                    ? "var(--primary-color)"
                    : "var(--surface-50, #f8f9fa)",
                color:
                  doc.id === documentId
                    ? "var(--primary-color-text)"
                    : "var(--text-color)",
                margin: "2px",
                padding: "4px 10px", // Reduced padding for a more compact look
                borderRadius: "12px",
                boxShadow:
                  doc.id === documentId ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s ease",
                border:
                  doc.id === documentId
                    ? "none"
                    : "1px solid var(--surface-border, #dee2e6)", // Added subtle border to non-selected chips
                fontSize: "0.875rem", // Keeping the same font size
                // height: "1.75rem", // Reduced height
                lineHeight: "1", // Tightened line height
              }}
              // Add onMouseEnter and onMouseLeave event handlers
              onMouseEnter={(e) => {
                if (doc.id !== documentId) {
                  e.currentTarget.style.backgroundColor =
                    "var(--surface-200, #e9ecef)";
                }
              }}
              onMouseLeave={(e) => {
                if (doc.id !== documentId) {
                  e.currentTarget.style.backgroundColor =
                    "var(--surface-50, #f8f9fa)";
                }
              }}
            />
          ))}
        </Box>

        {/* Document Content */}
        <GaDocument content={content} />
      </Box>

      {/* Table of Contents Drawer */}
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          width: 300,
          flexShrink: 0,
          zIndex: 1000,
          "& .MuiDrawer-paper": {
            width: 300,
            boxSizing: "border-box",
            position: "relative",
            backgroundColor: "transparent",
            border: "none",
          },
        }}
      >
        <Box sx={{ overflow: "auto", m: 2, padding: 0 }}>
          <Typography
            variant="body1"
            sx={{ mt: 2, mb: 1, fontWeight: "bold", textAlign: "left" }}
          >
            Table of Contents
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <SimpleTreeView>{renderTree(toc)}</SimpleTreeView>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DocumentViewer;
